import { createBrowserRouter } from "react-router-dom";
import {
  LocaleEntryRedirect,
  LocaleFallbackRedirect,
  SellerLocaleLayout,
} from "./locale";
import { ProtectedLayout } from "./ProtectedLayout";
import { ProtectedEditorLayout } from "./ProtectedEditorLayout";
import { ProtectedOnboardingLayout } from "./ProtectedOnboardingLayout";
import { RouteErrorElement } from "@/shared/error/RouteErrorElement";
import { dashboardRoutes } from "../modules/dashboard/routes";
import { authRoutes } from "../modules/auth/routes";
import { categoriesRoutes } from "../modules/categories/routes";
import {
  couponsRoutes,
  discountsRoutes,
  freeShippingRoutes,
} from "../modules/coupons/routes";
import { customersRoutes } from "../modules/customers/routes";
import { inviteRoutes } from "../modules/invite/routes";
import { teamRoutes } from "../modules/team/routes";
import { cartsRoutes } from "../modules/carts/routes";
import { orderRoutes } from "../modules/orders/routes";
import { transactionsRoutes } from "../modules/transactions/routes";
import { paymentsRoutes } from "../modules/payments/routes";
import { productRoutes } from "../modules/products/routes";
import { profileRoutes } from "../modules/profile/routes";
import { pwaRoutes } from "../modules/pwa/routes";
import { reviewsRoutes } from "../modules/reviews/routes";
import { rootRoutes } from "../modules/root/routes";
import { settingsRoutes } from "../modules/settings/routes";
import { storeEditorRoutes } from "../modules/store-editor/routes";
import { accountRoutes } from "../modules/account/routes";
import { metaRoutes } from "../modules/meta/routes";
import { onboardingRoutes } from "../modules/onboarding/routes";
import { accessAliasRoutes } from "@/shared/access/accessRoutes";

// Attached to every route that renders a LAYOUT, not just the root: React
// Router hands an error to the nearest ancestor `errorElement`, and the deeper
// one still has `:storeSlug` in its params — which is the difference between
// "back to your dashboard" and a dead link. Without any of these, a throw in a
// layout renders React Router's default developer error screen.
const errorElement = <RouteErrorElement />;

export const sellerRouter = createBrowserRouter([
  {
    path: "/",
    element: <LocaleEntryRedirect />,
    errorElement,
  },
  {
    path: "/:locale",
    element: <SellerLocaleLayout />,
    errorElement,
    children: [
      ...rootRoutes,
      ...authRoutes,
      ...inviteRoutes,
      ...teamRoutes,
      {
        // Editor occupies the full viewport; uses its own auth wrapper that
        // skips the standard sidebar/topbar chrome (`AppShell`).
        path: ":storeSlug",
        element: <ProtectedEditorLayout />,
        errorElement,
        children: [...storeEditorRoutes],
      },
      {
        // Quick Setup onboarding occupies the full viewport (no sidebar),
        // matching the full-screen onboarding design.
        path: ":storeSlug",
        element: <ProtectedOnboardingLayout />,
        errorElement,
        children: [...onboardingRoutes],
      },
      {
        path: ":storeSlug",
        element: <ProtectedLayout />,
        errorElement,
        children: [
          ...dashboardRoutes,
          ...profileRoutes,
          ...settingsRoutes,
          ...categoriesRoutes,
          ...productRoutes,
          ...orderRoutes,
          ...cartsRoutes,
          ...transactionsRoutes,
          ...paymentsRoutes,
          ...reviewsRoutes,
          ...couponsRoutes,
          ...discountsRoutes,
          ...freeShippingRoutes,
          ...customersRoutes,
          ...accountRoutes,
          ...metaRoutes,
          ...pwaRoutes,
          ...accessAliasRoutes,
        ],
      },
      {
        path: "*",
        element: <LocaleFallbackRedirect />,
      },
    ],
  },
  {
    path: "*",
    element: <LocaleEntryRedirect />,
    errorElement,
  },
]);
